/* eslint-disable no-console */
/**
 * The StrobeMediaPlayer triggers a global method once it's loaded and it only has access to the global scope.
 *
 * E.g. it does not have access to our providers directly or the resolve / reject methods when a provider is instantiated.
 *
 * As a workaround, we stuff our providers, resolve/reject methods (aka deferreds), and our players in this global class
 * that the StrobeMediaPlayer can access if we instantiate it globally.
 */
const noop = () => {};
export default class StrobeMediaHelper {
  constructor() {
    this._providers = {};
    this._deferreds = {};
    this._players = {};
    this._eventHandlers = {};

    // To handle events on the JS bridge the handler has to be on the window
    window.onSBStrobePlayerStateChanged = (newState, playerId) => {
      if (newState === 'playing') {
        this.getEventHandler(playerId, 'playing');
      }
    };
  }

  callbackHandler(playerId, eventName, data) {
    let strobePlayer = this.getPlayer(playerId);

    // Cache the strobe player to prevent constantly reaching into the DOM
    if (!strobePlayer) {
      strobePlayer = document.getElementById(playerId);
      this.registerPlayer(playerId, strobePlayer);
    }

    if (strobePlayer) {
      switch (eventName) {
        case 'onJavaScriptBridgeCreated':
          // Todo: maybe handle the mediaError event on the JS bridge
          // There are some events you can only get through the JS bridge and it seems,
          // the handler can only be defined on the window
          strobePlayer.addEventListener('playStateChange', 'onSBStrobePlayerStateChanged');
          break;
        case 'emptied': // The player is ready
          this.getDeferred(playerId).resolve(strobePlayer);
          break;
        case 'durationchange':
          this.getEventHandler(playerId, 'durationchange')(data);
          break;
        case 'play':
          this.getEventHandler(playerId, 'play')(data);
          break;
        case 'pause':
          this.getEventHandler(playerId, 'pause')(data);
          break;
        case 'timeupdate':
          this.getEventHandler(playerId, 'timeupdate')(data);
          break;
        case 'loadedmetadata':
          this.getEventHandler(playerId, 'loadedmetadata')(data);
          break;
        case 'complete':
          this.getEventHandler(playerId, 'ended')(data);
          break;
        case 'seeked':
          this.getEventHandler(playerId, 'seeked')(data);
          break;
        case 'volumechange':
          // Setting volume to 0 is tantamount to pausing
          // We'll send playing event every time the volume changes
          // which might be overkill, but it works
          // TODO: revisit in https://jira.savagebeast.com/browse/WEB-7457
          if (data.volume === 0) {
            this.getEventHandler(playerId, 'pause')(data);
          } else {
            this.getEventHandler(playerId, 'playing')(data);
          }
          break;
        case 'error':
          this.getEventHandler(playerId, 'error')(data);
          break;
        default:
      }
    } else {
      this.getDeferred(playerId).reject();
    }
  }

  getProvider(id) {
    return this._providers[id];
  }

  registerDeferred(id, def) {
    this._deferreds[id] = def;
  }

  registerEventHandlers(id, handlers) {
    this._eventHandlers[id] = handlers;
  }

  setEventHandler(id, handlerName, handlerFn) {
    if (!this._eventHandlers[id]) {
      this._eventHandlers[id] = {};
    }
    if (this._eventHandlers[id][handlerName]) {
      console.warn(`StrobePlayer: overwriting handler for player${id} and handler: ${handlerName}`);
    }
    this._eventHandlers[id][handlerName] = handlerFn;
  }

  getEventHandler(id, handlerName) {
    const eventHandlerForId = this._eventHandlers[id];
    if (eventHandlerForId && eventHandlerForId[handlerName]) {
      return eventHandlerForId[handlerName];
    }
    return noop;
  }

  removeEventHandler(id, handlerName) {
    const eventHandlerForId = this._eventHandlers[id];
    if (eventHandlerForId && eventHandlerForId[handlerName]) {
      eventHandlerForId[handlerName] = noop;
    }
  }

  getDeferred(id) {
    return this._deferreds[id];
  }

  registerPlayer(id, player) {
    this._players[id] = player;
  }

  getPlayer(id) {
    return this._players[id];
  }
}
